import React, { useState, useCallback } from 'react';
import { Schedule } from '../../types';

/**
 * Props for the ScheduleIndicator component.
 */
interface ScheduleIndicatorProps {
  /** The schedule data, or null if no schedule exists */
  schedule: Schedule | null;
  /** Size of the indicator icon */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show tooltip on hover/focus */
  showTooltip?: boolean;
}

/**
 * Clock icon SVG component for schedule indicator.
 */
const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    data-testid="clock-icon"
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" strokeWidth="2" />
    <path strokeLinecap="round" strokeWidth="2" d="M12 6v6l4 2" />
  </svg>
);

/**
 * Formats a schedule type for display.
 */
function formatScheduleType(type: string): string {
  switch (type) {
    case 'interval':
      return 'Interval';
    case 'cron':
      return 'Cron';
    case 'one_time':
      return 'One Time';
    default:
      return type;
  }
}

/**
 * Formats interval minutes for display.
 */
function formatInterval(minutes: number | null): string | null {
  if (minutes === null) return null;
  if (minutes === 60) return 'Every hour';
  return `Every ${minutes} minutes`;
}

/**
 * Formats a date string for display.
 */
function formatDateTime(dateStr: string | null): string | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Size class mapping for the icon.
 */
const sizeClasses: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

/**
 * ScheduleIndicator Component (AC-451)
 *
 * Displays a visual indicator showing whether a workflow has a schedule
 * configured and its current state (enabled/disabled). Shows a tooltip with
 * schedule details on hover or focus.
 */
const ScheduleIndicator: React.FC<ScheduleIndicatorProps> = ({
  schedule,
  size = 'md',
  showTooltip = true,
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const handleShowTooltip = useCallback(() => {
    if (showTooltip) {
      setIsTooltipVisible(true);
    }
  }, [showTooltip]);

  const handleHideTooltip = useCallback(() => {
    setIsTooltipVisible(false);
  }, []);

  // Return null when no schedule exists
  if (!schedule) {
    return null;
  }

  const isEnabled = schedule.is_enabled;
  const colorClass = isEnabled ? 'text-green-600' : 'text-gray-400';
  const iconSizeClass = sizeClasses[size];

  // Build aria-label for accessibility
  const ariaLabel = isEnabled
    ? 'Schedule enabled - active'
    : 'Schedule disabled';

  // Format tooltip content
  const scheduleTypeDisplay = formatScheduleType(schedule.schedule_type);
  const intervalDisplay =
    schedule.schedule_type === 'interval'
      ? formatInterval(schedule.interval_minutes)
      : null;
  const nextRunDisplay = !isEnabled
    ? 'Paused'
    : schedule.next_run_at
    ? formatDateTime(schedule.next_run_at)
    : 'Not scheduled';
  const lastRunDisplay = schedule.last_run_at
    ? formatDateTime(schedule.last_run_at)
    : null;
  const neverRun = !schedule.last_run_at;

  return (
    <div
      data-testid="schedule-indicator"
      className={`relative inline-flex items-center cursor-pointer ${colorClass}`}
      aria-label={ariaLabel}
      tabIndex={0}
      onMouseEnter={handleShowTooltip}
      onMouseLeave={handleHideTooltip}
      onFocus={handleShowTooltip}
      onBlur={handleHideTooltip}
    >
      <ClockIcon className={iconSizeClass} />

      {/* Tooltip */}
      {isTooltipVisible && showTooltip && (
        <div
          role="tooltip"
          className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded shadow-lg whitespace-nowrap z-50"
        >
          <div className="space-y-1">
            {/* Schedule Type */}
            <div className="font-medium">{scheduleTypeDisplay}</div>

            {/* Interval (for interval schedules) */}
            {intervalDisplay && (
              <div className="text-gray-300">{intervalDisplay}</div>
            )}

            {/* Next Run */}
            <div className="text-gray-300">
              Next run: {nextRunDisplay}
            </div>

            {/* Last Run Status */}
            {neverRun ? (
              <div className="text-gray-300">Never run</div>
            ) : (
              <div className="flex items-center gap-1">
                <span className="text-gray-300">Last run: </span>
                {schedule.last_run_status && (
                  <span
                    className={
                      schedule.last_run_status === 'failed'
                        ? 'text-red-600'
                        : schedule.last_run_status === 'completed'
                        ? 'text-green-400'
                        : 'text-gray-300'
                    }
                  >
                    {schedule.last_run_status}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Tooltip arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  );
};

export default ScheduleIndicator;
